import React, { Suspense, lazy, useState } from 'react';
import { BedDouble, CalendarDays, Check, ChevronDown, FileCheck2, Lightbulb, MapPin, Route as RouteIcon, Sparkles, Users, Utensils, X } from 'lucide-react';

const RouteMap = lazy(() => import('./RouteMap'));

const list = (v) => (Array.isArray(v) ? v : []).filter((x) => x !== null && x !== undefined && String(x).trim() !== '');

/** Route as "Srinagar → Gulmarg → Pahalgam", collapsing repeats next to each other. */
export const routeText = (route) => list(route).map((r) => r.place).filter((p, i, a) => p && p !== a[i - 1]).join(' → ');

const SECTIONS = [
  ['pk-overview', 'Overview'],
  ['pk-days', 'Day by day'],
  ['pk-map', 'Route map'],
  ['pk-stays', 'Stays'],
  ['pk-inc', 'Inclusions'],
  ['pk-info', 'Good to know'],
  ['pk-faq', 'FAQs'],
];

/**
 * Main column of a package page when rewritten content exists
 * (src/content/packages/<id>.json).
 */
const PackageDetails = ({ c, pkg }) => {
  const [lang, setLang] = useState('en');
  const te = lang === 'te';
  const days = list(c.days);
  const route = list(c.route);
  const stays = list(c.stays);
  const faqs = list(c.faqs);
  const inc = list(c.inclusions);
  const exc = list(c.exclusions);
  const tips = list(c.goodToKnow);
  const hasTe = days.some((d) => d.te && d.te.plan);
  const present = new Set([
    'pk-overview',
    days.length && 'pk-days',
    route.length && 'pk-map',
    stays.length && 'pk-stays',
    (inc.length || exc.length) && 'pk-inc',
    (c.visa || tips.length) && 'pk-info',
    faqs.length && 'pk-faq',
  ].filter(Boolean));

  const LangSwitch = hasTe ? (
    <div className="vk-lang" role="group" aria-label="Language">
      <button type="button" className={!te ? 'is-active' : ''} aria-pressed={!te} onClick={() => setLang('en')}>English</button>
      <button type="button" className={te ? 'is-active' : ''} aria-pressed={te} onClick={() => setLang('te')} lang="te">తెలుగు</button>
    </div>
  ) : null;

  return (
    <div className="vk-pd">
      <dl className="vk-pd__facts">
        <div><dt><CalendarDays size={18} /> Duration</dt><dd>{pkg.duration}</dd></div>
        {route.length ? <div><dt><RouteIcon size={18} /> Route</dt><dd>{routeText(route)}</dd></div> : null}
        {list(c.bestFor).length ? <div><dt><Users size={18} /> Best for</dt><dd>{list(c.bestFor).join(', ')}</dd></div> : null}
        {c.bestTime ? <div><dt><Sparkles size={18} /> Best time</dt><dd>{c.bestTime}</dd></div> : null}
      </dl>

      <nav className="vk-pd__nav" aria-label="On this page">
        {SECTIONS.filter(([id]) => present.has(id)).map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}
      </nav>

      <section id="pk-overview" className="vk-pd__sec">
        <div className="vk-pd__head"><h2>Overview</h2>{LangSwitch}</div>
        <p className="vk-pd__lede" lang={te ? 'te' : undefined}>{te && c.te?.summary ? c.te.summary : c.summary}</p>
        {list(c.highlights).length ? (
          <ul className="vk-pd__hl">
            {list(c.highlights).map((h) => <li key={h}><Check size={18} /> {h}</li>)}
          </ul>
        ) : null}
      </section>

      {days.length ? (
        <section id="pk-days" className="vk-pd__sec">
          <div className="vk-pd__head"><h2 lang={te ? 'te' : undefined}>{te ? 'రోజువారీ ప్రణాళిక' : 'Day-by-day itinerary'}</h2>{LangSwitch}</div>
          <ol className="vk-pd__days">
            {days.map((d) => {
              const title = te && d.te?.title ? d.te.title : d.title;
              const plan = te && d.te?.plan ? d.te.plan : d.plan;
              return (
                <li key={d.day} className="vk-pd__day">
                  <span className="vk-pd__daynum" aria-hidden="true">{d.day}</span>
                  <div className="vk-pd__daybody">
                    <p className="vk-pd__dayk">{te ? <span lang="te">{`${d.day}వ రోజు`}</span> : `Day ${d.day}`}{d.place ? ` · ${d.place}` : ''}</p>
                    <h3 lang={te ? 'te' : undefined}>{title}</h3>
                    <p lang={te ? 'te' : undefined}>{plan}</p>
                    {(list(d.meals).length || d.stay) ? (
                      <p className="vk-pd__daymeta">
                        {list(d.meals).length ? <span><Utensils size={15} /> {list(d.meals).join(', ')}</span> : null}
                        {d.stay ? <span><BedDouble size={15} /> {d.stay}</span> : null}
                      </p>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}

      {route.length ? (
        <section id="pk-map" className="vk-pd__sec">
          <div className="vk-pd__head"><h2>Route map</h2></div>
          <Suspense fallback={<div className="vk-map vk-map--loading" />}>
            <RouteMap route={route} label={`${pkg.title} route`} />
          </Suspense>
          <ol className="vk-pd__stops">
            {route.map((r, i) => (
              <li key={`${r.place}-${i}`}><span>{i + 1}</span><b>{r.place}</b>{r.nights ? <em>{r.nights} night{r.nights > 1 ? 's' : ''}</em> : <em>{i === route.length - 1 ? 'end' : 'visit'}</em>}</li>
            ))}
          </ol>
        </section>
      ) : null}

      {stays.length ? (
        <section id="pk-stays" className="vk-pd__sec">
          <div className="vk-pd__head"><h2>Where you'll stay</h2></div>
          <div className="vk-pd__stays">
            {stays.map((s, i) => (
              <div key={`${s.place}-${i}`} className="vk-pd__stay">
                <MapPin size={18} />
                <div><b>{s.place}</b><span>{s.nights} night{s.nights > 1 ? 's' : ''} · {s.type}</span></div>
              </div>
            ))}
          </div>
          <p className="vk-form__note" style={{ marginTop: 10 }}>Hotel names are confirmed with your quote; upgrades available on request.</p>
        </section>
      ) : null}

      {inc.length || exc.length ? (
        <section id="pk-inc" className="vk-pd__sec">
          <div className="vk-pd__head"><h2>What's included</h2></div>
          <div className="vk-pkg__inc">
            {inc.length ? <div className="vk-panel"><h3>Included</h3><ul className="vk-ticks vk-ticks--yes">{inc.map((x) => <li key={x}><Check size={18} /> {x}</li>)}</ul></div> : null}
            {exc.length ? <div className="vk-panel"><h3>Not included</h3><ul className="vk-ticks vk-ticks--no">{exc.map((x) => <li key={x}><X size={18} /> {x}</li>)}</ul></div> : null}
          </div>
        </section>
      ) : null}

      {c.visa || tips.length ? (
        <section id="pk-info" className="vk-pd__sec">
          <div className="vk-pd__head"><h2>Good to know</h2></div>
          {c.visa ? (
            <div className="vk-pd__visa"><FileCheck2 size={22} /><div><b>Visa & entry for Indian travellers</b><p>{c.visa}</p></div></div>
          ) : null}
          {tips.length ? <ul className="vk-ticks" style={{ marginTop: 16 }}>{tips.map((t) => <li key={t}><Lightbulb size={18} color="var(--hot)" /> {t}</li>)}</ul> : null}
        </section>
      ) : null}

      {faqs.length ? (
        <section id="pk-faq" className="vk-pd__sec">
          <div className="vk-pd__head"><h2>Questions travellers ask</h2></div>
          <div className="vk-pd__faq">
            {faqs.map((f, i) => (
              <details key={f.q} open={i === 0}>
                <summary>{f.q}<ChevronDown size={18} aria-hidden="true" /></summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default PackageDetails;
